<script setup lang="ts">
import { add } from 'date-fns';
import { cloneDeep, isEqual } from 'lodash-es';
import { computed, ref, watch } from 'vue';
import FilterComp from './FilterComp/FilterComp.vue';
import useEvents from '/@composables/useEvents';
import DfCalendar from '/@elements/DfCalendar/DfCalendar.vue';
import ResourceDialog from '/@features/ResourceDialog/ResourceDialog.vue';
import CreateEvent from '/@pages/SchedulePage/CreateEvent/CreateEvent.vue';
import EventDialog from '/@pages/SchedulePage/EventDialog/EventDialog.vue';
import {
  loadResourceEvents,
  loadResourceEventsByGuid,
  loadResourceFilter,
  loadResources,
} from '/@stores/resources';
import { awaitTenant, useTenantStore } from '/@stores/tenant';
import { OptType } from '/@types/opt';
import { DefaultWorkingHours, Resource } from '/@types/resources';
import { createEventForm } from '/@utilities/events';
import { optFromLocalStorage, optToLocalStorage } from '/@utilities/opt';
import { toast } from '/@utilities/toast';

const tenantStore = useTenantStore();

const baseOpt = {
  dateRange: {
    start: new Date(),
    end: new Date(),
  },
  filters: [],
};

const resources = ref<Resource[]>([]);

const defaultWorkingHours = ref<DefaultWorkingHours | void>({
  startTime: {
    hours: 8,
    minutes: 0,
  },
  endTime: {
    hours: 16,
    minutes: 0,
  },
});

const opt = ref({
  ...baseOpt,
  ...optFromLocalStorage(OptType.List, 'search/opt'),
});

const loading = ref(0);

const events = useEvents();

watch(
  () => cloneDeep(opt.value),
  (currentValue, oldValue) => {
    if (isEqual(oldValue, currentValue)) return;
    optToLocalStorage('search/opt', currentValue);
    load();
  },
  { deep: true },
);

const sortResourcesByName = (a: Resource, b: Resource) => a.name.localeCompare(b.name);

const filteredResources = computed(() => {
  const resourceTypes =
    opt.value.filters.find(({ columnId }) => columnId === 'resourceType')?.values ?? [];
  const filterIds =
    opt.value.filters.find(({ columnId }) => columnId === 'filterIds')?.values ?? [];
  const nameSearch = opt.value.filters.find(({ columnId }) => columnId === 'userName')?.values[0];

  const typeFilter = (r) => resourceTypes.length === 0 || resourceTypes.includes(r.type);
  const idFilter = (r) =>
    filterIds.length === 0 || filterIds.some((id) => r.filters.some(({ id: fid }) => fid === id));
  const nameFilter = (r) =>
    !nameSearch || r.name?.toLowerCase().includes(String(nameSearch).toLowerCase());

  return resources.value.filter((r) => {
    if (!typeFilter(r) || !idFilter(r) || !nameFilter(r)) return false;
    if (!showHidden.value) return events.events.value.some((i) => i.resourceGuid === r.guid);
    return true;
  });
});

const filters = ref([]);

function changeDate({ start, end }) {
  opt.value.dateRange = { start, end };
}

const showHidden = ref(false);

const showCreateEvent = ref(false);

const selectedResource = ref(null);

function setSelectedResource(event) {
  createEventForm(
    {
      toDate: add(event.date, { hours: 8 }),
      fromDate: event.date,
      resourceGuid: event.resource.guid,
      resourceType: event.resource.type,
      userName: event.resource.name,
    },
    resources.value,
    defaultWorkingHours.value,
  ).then((res) => {
    selectedResource.value = res;
    showCreateEvent.value = true;
  });
}

function submitWrapper(fn: any, create = false) {
  loading.value++;

  return fn
    .then(() => {
      toast(`Avtale ${create ? 'opprettet' : 'oppdatert'}`);
    })
    .catch((error) => {
      console.error(error);
    })
    .finally(() => {
      loading.value--;
    });
}

function getEvent(eventGuid: string) {
  if (events.events.value.some(({ guid }) => guid === eventGuid)) return Promise.resolve();

  return loadResourceEventsByGuid(eventGuid).then((event) => {
    if (!event) return;
    events.events.value.push(event);
  });
}

function load() {
  loading.value++;

  return Promise.all([
    loadResources({ type: 'equipment' }),
    loadResources({ type: 'user' }),
    loadResourceEvents(opt.value),
  ])
    .then(([equipmentEntries, userEntries, eventEntries]) => {
      resources.value = [
        ...(equipmentEntries || []).sort(sortResourcesByName),
        ...(userEntries || []).sort(sortResourcesByName),
      ];
      events.events.value = eventEntries;
    })
    .finally(() => {
      loading.value--;
    });
}

async function initialLoad() {
  await awaitTenant;

  defaultWorkingHours.value = tenantStore.getDefaultWorkingHours();

  load().then(() => {
    loadResourceFilter().then((i) => (filters.value = i || []));
  });
}

initialLoad();
</script>

<template>
  <div class="page--wide">
    <h1 class="title title-lg">
      Filtrér avtaler
      <div class="badge">Beta</div>
    </h1>

    <df-loading v-if="loading > 0" />

    <section class="aa">
      <filter-comp :opt="opt">
        <df-checkbox v-model="showHidden" elevate>Vis ressurser uten avtaler</df-checkbox>
      </filter-comp>
    </section>

    <df-calendar
      :events="events.events.value"
      :resources="filteredResources"
      view="dfResourceWeekEaDay"
      buttons="dfResourceMonth,dfResourceWeekEaDay,dfResourceWeekEaHour"
      v-on:date-change="changeDate($event)"
      v-on:date-click="setSelectedResource($event)"
      v-on:event-edit="submitWrapper(events.fromExisting($event))"
    >
      <template v-slot:resource="{ resource, close }">
        <resource-dialog :resource="resource" :resource-filters="filters" :close="close" />
      </template>

      <template v-slot:event="{ event, close, setActiveEvent }">
        <event-dialog
          :event="event"
          :close="close"
          :resources="filteredResources"
          :set-active-event="setActiveEvent"
          :update="load"
          :delete="events.deleteEventItem"
          :unlink="events.unlinkEventItem"
          v-on:new-event="
            getEvent(event.mainEventGuid).then(() =>
              setActiveEvent(events.events.value.find(({ guid }) => event.mainEventGuid === guid)),
            )
          "
        />
      </template>
    </df-calendar>

    <df-modal v-if="showCreateEvent && selectedResource">
      <create-event
        :selected-resource="selectedResource"
        :saving="loading > 0"
        v-on:create="
          submitWrapper(events.fromForm($event, true), true).then(() => (showCreateEvent = false))
        "
        v-on:close="showCreateEvent = false"
      />
    </df-modal>
  </div>
</template>

<style scoped>
.page--wide {
  display: grid;
  grid-template-columns: 30ch 1fr;
  gap: var(--gap-lg);
  padding: var(--gap-lg);
  height: 100vh;
  align-content: start;
  grid-template-rows: max-content 1fr;
}

.map {
  z-index: 0;
}

.badges {
  display: flex;
  gap: var(--gap-sm);
  flex-wrap: wrap;
}

.badge {
  cursor: default;
}

.--active {
  background-color: var(--color-primary);
}

.title {
  grid-column: span 2;
}
</style>
